import { useState, useRef } from "react";
import { ToolsShell, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// EXIF Remover — local-only metadata stripper for JPEG and PNG.
//
// Approach: parse JPEG marker structure directly (no library). EXIF is
// stored in an APP1 marker (0xFFE1) right after SOI (0xFFD8). We also
// remove APP0 (JFIF — usually fine), APP13 (Photoshop IPTC), and any
// COM (comment) markers. The result is just SOI + scan + remaining
// image data.
//
// For PNG, we parse chunks and skip tEXt, iTXt, zTXt, eXIf, tIME chunks
// (the metadata ones), keeping IHDR, IDAT, PLTE, IEND, and color-space
// chunks intact.
//
// We also extract a summary of what we *found* before stripping, so the
// user sees concretely what privacy info was in the file (GPS location,
// camera model, timestamp, etc.).

// EXIF tag IDs we care about for the summary
const EXIF_TAGS = {
  271: "Camera make",
  272: "Camera model",
  306: "Date taken",
  36867: "Original date",
  36868: "Digitized date",
  305: "Software",
  315: "Author",
  33432: "Copyright",
};

// Parse a uint16 from a DataView at offset
const u16 = (dv, off, le) => dv.getUint16(off, le);
const u32 = (dv, off, le) => dv.getUint32(off, le);

/** Scan EXIF data block for a summary of what's there. */
function scanExif(buf) {
  // buf is the APP1 segment content starting with "Exif\0\0" then TIFF header
  if (buf.byteLength < 10) return [];
  const dv = new DataView(buf);
  // First 6 bytes: "Exif\0\0"
  if (dv.getUint8(0) !== 0x45 || dv.getUint8(1) !== 0x78) return [];
  const tiffStart = 6;
  if (tiffStart + 8 > buf.byteLength) return [];
  // Endianness marker
  const endian = u16(dv, tiffStart, false);
  const le = endian === 0x4949; // "II" little-endian
  if (!le && endian !== 0x4D4D) return [];
  // First IFD offset
  const ifdOffset = u32(dv, tiffStart + 4, le);
  if (tiffStart + ifdOffset + 2 > buf.byteLength) return [];
  const findings = new Set();
  const numEntries = u16(dv, tiffStart + ifdOffset, le);
  let entryOff = tiffStart + ifdOffset + 2;
  let hasGPS = false;
  for (let i = 0; i < numEntries && entryOff + 12 <= buf.byteLength; i++) {
    const tag = u16(dv, entryOff, le);
    if (EXIF_TAGS[tag]) findings.add(EXIF_TAGS[tag]);
    if (tag === 34853) hasGPS = true; // GPS IFD pointer
    entryOff += 12;
  }
  if (hasGPS) findings.add("GPS location");
  return Array.from(findings);
}

/** Strip JPEG metadata. Returns { cleaned: ArrayBuffer, findings: string[] }. */
function cleanJpeg(buf) {
  const dv = new DataView(buf);
  if (dv.getUint8(0) !== 0xFF || dv.getUint8(1) !== 0xD8) {
    throw new Error("Not a valid JPEG.");
  }
  const findings = new Set();
  const keptChunks = [];
  // SOI
  keptChunks.push(new Uint8Array(buf, 0, 2));
  let off = 2;
  while (off < buf.byteLength) {
    if (dv.getUint8(off) !== 0xFF) break; // Bad data; just append remainder
    const marker = dv.getUint8(off + 1);
    // SOS = 0xDA — image data starts; rest of file is kept as-is
    if (marker === 0xDA) {
      keptChunks.push(new Uint8Array(buf, off, buf.byteLength - off));
      break;
    }
    // RST markers don't have a length
    if (marker >= 0xD0 && marker <= 0xD7) {
      keptChunks.push(new Uint8Array(buf, off, 2));
      off += 2;
      continue;
    }
    // Otherwise has a 2-byte length
    const segLen = dv.getUint16(off + 2, false);
    const isMetadata =
      marker === 0xE1 || // APP1: EXIF / XMP
      marker === 0xE0 || // APP0: JFIF (mostly harmless but contains thumbnail in some)
      marker === 0xED || // APP13: IPTC/Photoshop
      marker === 0xEE || // APP14: Adobe
      marker === 0xFE;   // COM: comment
    if (marker === 0xE1) {
      // Inspect EXIF for summary before discarding
      try {
        const data = buf.slice(off + 4, off + 2 + segLen);
        const list = scanExif(data);
        list.forEach((f) => findings.add(f));
        findings.add("EXIF block");
      } catch {}
    } else if (marker === 0xE0) {
      findings.add("JFIF block");
    } else if (marker === 0xED) {
      findings.add("IPTC/Photoshop metadata");
    } else if (marker === 0xFE) {
      findings.add("Comment");
    }
    if (!isMetadata) {
      keptChunks.push(new Uint8Array(buf, off, 2 + segLen));
    }
    off += 2 + segLen;
  }
  // Combine
  const total = keptChunks.reduce((s, c) => s + c.byteLength, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  for (const c of keptChunks) { out.set(c, pos); pos += c.byteLength; }
  return { cleaned: out.buffer, findings: Array.from(findings) };
}

/** Strip PNG metadata chunks. */
function cleanPng(buf) {
  const dv = new DataView(buf);
  // PNG signature: 89 50 4E 47 0D 0A 1A 0A
  const sig = [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A];
  for (let i = 0; i < 8; i++) {
    if (dv.getUint8(i) !== sig[i]) throw new Error("Not a valid PNG.");
  }
  const findings = new Set();
  const KEEP = new Set(["IHDR", "PLTE", "IDAT", "IEND", "tRNS", "gAMA", "cHRM", "sRGB", "iCCP", "bKGD", "pHYs", "sBIT", "sPLT", "hIST"]);
  const STRIP_LABEL = {
    tEXt: "Text metadata",
    iTXt: "International text metadata",
    zTXt: "Compressed text metadata",
    eXIf: "EXIF block",
    tIME: "Last-modified time",
  };
  const keptChunks = [new Uint8Array(buf, 0, 8)]; // signature
  let off = 8;
  while (off < buf.byteLength) {
    const len = dv.getUint32(off, false);
    const type = String.fromCharCode(dv.getUint8(off + 4), dv.getUint8(off + 5), dv.getUint8(off + 6), dv.getUint8(off + 7));
    const totalChunk = 4 + 4 + len + 4; // length + type + data + CRC
    if (KEEP.has(type)) {
      keptChunks.push(new Uint8Array(buf, off, totalChunk));
    } else {
      if (STRIP_LABEL[type]) findings.add(STRIP_LABEL[type]);
      else findings.add(`${type} chunk`);
    }
    off += totalChunk;
    if (type === "IEND") break;
  }
  const total = keptChunks.reduce((s, c) => s + c.byteLength, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  for (const c of keptChunks) { out.set(c, pos); pos += c.byteLength; }
  return { cleaned: out.buffer, findings: Array.from(findings) };
}

export default function ExifRemover() {
  const [file, setFile] = useState(null);
  const [findings, setFindings] = useState([]);
  const [cleaned, setCleaned] = useState(null); // ArrayBuffer
  const [err, setErr] = useState(null);
  const [originalSize, setOriginalSize] = useState(0);
  const [cleanedSize, setCleanedSize] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);

  const onFile = async (e) => {
    setErr(null); setCleaned(null); setFindings([]); setPreviewUrl(null);
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setFile(f);
    setOriginalSize(f.size);
    const buf = await f.arrayBuffer();
    try {
      let result;
      const lower = f.name.toLowerCase();
      const isPng = lower.endsWith(".png") || (buf.byteLength > 8 && new DataView(buf).getUint8(0) === 0x89);
      if (isPng) {
        result = cleanPng(buf);
      } else {
        result = cleanJpeg(buf);
      }
      setCleaned(result.cleaned);
      setFindings(result.findings);
      setCleanedSize(result.cleaned.byteLength);
      const blob = new Blob([result.cleaned], { type: isPng ? "image/png" : "image/jpeg" });
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (ex) {
      setErr(ex.message || "Couldn't process this file. Only JPEG and PNG are supported.");
    }
  };

  const download = () => {
    if (!cleaned || !file) return;
    const isPng = file.name.toLowerCase().endsWith(".png");
    const blob = new Blob([cleaned], { type: isPng ? "image/png" : "image/jpeg" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    const base = file.name.replace(/\.(jpe?g|png)$/i, "");
    a.download = `${base}-clean.${isPng ? "png" : "jpg"}`;
    a.click();
  };

  return (
    <ToolsShell title="EXIF Remover">
      <div style={{ padding: "12px 14px", background: "#13141F", border: "1px solid #262839", borderRadius: 10, fontSize: 13, color: "#C8CADF", marginBottom: 16 }}>
        <strong style={{ color: "#5BD99A" }}>● Local processing.</strong> Your file never leaves your device — EXIF stripping happens entirely in your browser.
      </div>

      {!file ? (
        <div style={{ textAlign: "center", marginTop: 10 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Strip EXIF metadata (camera model, GPS location, timestamps, software) from a JPEG or PNG before sharing.
          </p>
          <label style={{ ...primaryBtn, display: "inline-block" }}>
            <input type="file" accept="image/jpeg,image/png" onChange={onFile} style={{ display: "none" }} />
            Choose photo
          </label>
        </div>
      ) : (
        <>
          {err ? (
            <div style={{ padding: "12px 14px", background: "#1F1418", border: "1px solid #E0584E33", borderRadius: 10, color: "#D9A39E" }}>
              {err}
            </div>
          ) : (
            <>
              {previewUrl && (
                <div style={{ position: "relative", maxWidth: "100%", margin: "0 auto 14px", textAlign: "center" }}>
                  <img src={previewUrl} alt="Cleaned preview" style={{ maxWidth: "100%", maxHeight: "40vh", borderRadius: 10, background: "#000" }} />
                </div>
              )}

              {/* Findings */}
              <div style={{ background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 12, padding: "16px 18px" }}>
                <div style={{ fontSize: 12, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
                  Metadata removed
                </div>
                {findings.length === 0 ? (
                  <div style={{ fontSize: 14, color: "#C8CADF" }}>
                    No metadata blocks found — the file was already clean.
                  </div>
                ) : (
                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    {findings.map((f, i) => (
                      <li key={i} style={{ padding: "6px 0", fontSize: 14, color: "#E8E9F3", display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 6, height: 6, background: f.toLowerCase().includes("gps") ? "#E0584E" : TOOLS_ACCENT, borderRadius: "50%", display: "inline-block" }} />
                        {f}
                        {f.toLowerCase().includes("gps") && (
                          <span style={{ fontSize: 11, color: "#E0584E", marginLeft: 4 }}>(location data!)</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #1F2030", fontSize: 12, color: "#7C8099" }}>
                  Original: {(originalSize / 1024).toFixed(1)} KB · Clean: {(cleanedSize / 1024).toFixed(1)} KB
                  {originalSize > cleanedSize && ` · saved ${((originalSize - cleanedSize) / 1024).toFixed(1)} KB`}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                <button onClick={download} style={{ ...primaryBtn, flex: 1 }}>Download clean file</button>
                <label style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer", textAlign: "center" }}>
                  <input type="file" accept="image/jpeg,image/png" onChange={onFile} style={{ display: "none" }} />
                  Different photo
                </label>
              </div>
            </>
          )}
        </>
      )}

      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14 }}>
        Strips EXIF, XMP, IPTC, and embedded text from JPEG and PNG. Pixels themselves are untouched — image quality is unchanged. Some apps re-add metadata when you re-share; download fresh after editing.
      </p>
    </ToolsShell>
  );
}
